import BaseApiAction from '@Actions/base-api-action';
import { Actions, Events } from '@Context/common/general/context';
import * as Sentry from "@sentry/react";
import { isEmpty } from 'lodash-es';

export default class GetAccountInfo extends BaseApiAction {
  runAction() {
    super.invokeCloudFunction({
      name: 'users-temp-getAccountInfo',
      payload: {
        skip_chaos: true
      }
  });
  }

  onSuccess(data: any) {
    // Set Sentry User ID for this session
    Sentry.setUser({ id: data.doc_id });
    Sentry.setTag("user_id", data.doc_id);
    
    // Set Amplitude User ID for  this session
    this._amplitude.setUserId(data.doc_id);

    this._amplitude.setUserProperties({
      email: data.email,
    });

    // Determine if account holder user needs to create an access team
    let providerTeamId = "";
    let mergedTeams = data.teams;
    if( !isEmpty(data.admin_for_teams) ) {
      mergedTeams = [...mergedTeams, ...data.admin_for_teams];
    }

    mergedTeams.forEach((team: any) => {
      if( team.license_provider && team.license_provider === data.doc_id ) {
        providerTeamId = team.doc_id;
      }
    });

    /**
     * Update user data in context
     */
    const userInfo = {
      data: data,
      userRating: data.user_rating || 0,
      level: data.level || 1,
      authenticated: true,
      displayName: `${data.first} ${data.last}`,
      isSuperAdmin: data.role === 'SUPER_ADMIN',
      adminForClubs: data.admin_for_clubs,
      adminForTeams: data.admin_for_teams,
      teams: data.teams,
      creationDate: data.creation_date,
      dob: data.dob,
      id: data.doc_id,
      email: data.email,
      first: data.first,
      gender: data.gender,
      last: data.last,
      countryCode: data.country_code || null,
      licenseProvider: data.license_provider,
      role: data.role,
      accountLinkedUuIds: this._state.app.applicationIdentifier ? data[`linked_uuid_${(this._state.app.applicationIdentifier as string).split(".").join("_")}`] : [], 
      providerTeamId: providerTeamId,
      kit: null,
      inBootcamp: (data.user_rating || 0) < 2000
    };

    const updateUserState = (userData: any) => {
      this._dispatch({
        type: Actions.CoreActions.UpdateState,
        payload: {
          app: {
            initialized: true,
          },
          user: {
            ...userData
          },
        },
        disableLoaders: this._disableLoaders,
        onSuccess: (updatedState: any) => {
          // Process queued analytics events after user state is updated with authentication info
          if (window.processAnalyticsQueue && updatedState.user.authenticated && updatedState.user.id) {
            window.processAnalyticsQueue(updatedState);
          }
        }
      });

      // Refresh the current account to get the latest (authorizations) subscriptions
      this._state.account.events.refreshCurrentAccount.emit({
        onSuccess: () => {
          super.onSuccess(data);
        }
      });
      this._dispatch({
        type: Actions.TeamsActions.GetTeamConfigs,
        payload: {
          user_doc_id: userData.id,
          resolve_user_account: false,
          limit: 1
        },
        disableLoaders: this._disableLoaders,
        onSuccess: (teamConfigs) => {
          if( teamConfigs && teamConfigs.length > 0 ) {
            userInfo.kit = teamConfigs[0].home_kit || null;
          }
          // Update Account info for Unity bridge purposes
          Events.auth.accountInfoUpdated.emit(userInfo);
        },
        onError: () => {
          // Update Account info for Unity bridge purposes
          Events.auth.accountInfoUpdated.emit(userInfo);
        }
      });
    }

    this._dispatch({
      type: Actions.UsersActions.GetUser,
      payload: {
        doc_id: userInfo.id,
      },
      disableLoaders: this._disableLoaders,
      onSuccess: data => updateUserState({ ...userInfo, dbId: data.id }),
      onError: error =>
        updateUserState({
          ...userInfo,
          dbId: null,
          needsUserRecordRecovery: error?.code === "functions/not-found",
        }),
    });
  }

  onError(data: any) {
    this._dispatch({
      type: Actions.FirebaseActions.Logout,
    });
    super.onError(data);
  }
}
